import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Step1Statement } from "./wizard/Step1Statement";
import { Step2Backtest } from "./wizard/Step2Backtest";
import { Step3Commit } from "./wizard/Step3Commit";
import { useDraft, type CompileResult } from "@/monitors/api";
// Audit F1: writeStashedIR lives in Step2Backtest (per-edit). Wizard page
// just reads (on rehydration) and clears (on commit + back-to-step-1).
import { clearStashedIR, readStashedIR } from "@/monitors/hooks/useDraftIRStash";
import type { MonitorIR } from "@/monitors/schemas";

function irDiff(base: MonitorIR, edited: MonitorIR): Partial<MonitorIR> | null {
  const diff: Partial<MonitorIR> = {};
  for (const key of Object.keys(edited) as Array<keyof MonitorIR>) {
    if (JSON.stringify(edited[key]) !== JSON.stringify(base[key])) {
      (diff as any)[key] = edited[key];
    }
  }
  return Object.keys(diff).length > 0 ? diff : null;
}

export function MonitorWizardPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const step = Number(params.get("step") ?? "1");
  const draftIdParam = params.get("draft_id") ?? undefined;
  const [compiled, setCompiled] = useState<CompileResult | null>(null);
  // Step 2 → Step 3 hand-off: stash the (possibly edited) IR so Step 3 can
  // read it and compute the ir_overrides diff against compiled.ir at save.
  const [step3IR, setStep3IR] = useState<MonitorIR | null>(null);

  // Audit I4: rehydrate `compiled` from the backend when the URL has a
  // draft_id but the in-memory state is empty (page reload at step 2/3).
  // useDraft is no-op until draftIdParam exists AND compiled is null.
  const { data: hydratedDraft, isError: draftLoadError } = useDraft(
    !compiled && draftIdParam ? draftIdParam : undefined,
  );
  useEffect(() => {
    if (hydratedDraft && !compiled) {
      // DraftResponse shape mirrors CompileResult — same fields the wizard
      // already consumes; safe to drop into `compiled` state.
      setCompiled({
        draft_id: hydratedDraft.draft_id,
        canonical_statement: hydratedDraft.canonical_statement,
        ir: hydratedDraft.ir,
        suggested_candidate_threshold: hydratedDraft.suggested_candidate_threshold,
      });
      // Audit F1: prefer the sessionStorage-stashed IR (user's in-flight
      // edits) over the LLM-compiled IR returned by the backend. The
      // backend doesn't yet persist mid-wizard edits, so without this
      // sessionStorage fallback a page reload silently reverts every
      // chip the user added or removed in Step 2.
      const stashed = readStashedIR(hydratedDraft.draft_id);
      setStep3IR(stashed ?? hydratedDraft.ir);
    }
  }, [hydratedDraft, compiled]);

  const goToStep = (n: number, extra?: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    merged.set("step", String(n));
    for (const [k, v] of Object.entries(extra ?? {})) merged.set(k, v);
    setParams(merged);
  };

  const handleCompiled = (res: CompileResult) => {
    setCompiled(res);
    goToStep(2, { draft_id: res.draft_id });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button
        className="text-sm text-muted-foreground mb-4"
        onClick={() => navigate("/monitors")}
      >
        ← Back to monitors
      </button>
      <Stepper step={step} />
      <div className="mt-6">
        {step === 1 && <Step1Statement onCompiled={handleCompiled} />}
        {step === 2 && compiled && (
          <Step2Backtest
            compiled={compiled}
            onBack={() => {
              // Audit F1: going back to step 1 lets the user pick a
              // different statement which will produce a new draft id —
              // the old stash is stale, drop it.
              clearStashedIR(compiled.draft_id);
              goToStep(1);
            }}
            onNext={(editedIR) => {
              setStep3IR(editedIR);
              goToStep(3, { draft_id: compiled.draft_id });
            }}
          />
        )}
        {step === 2 && !compiled && draftIdParam && !draftLoadError && (
          <div className="text-sm text-muted-foreground">Recovering draft…</div>
        )}
        {step === 2 && !compiled && (!draftIdParam || draftLoadError) && (
          <div className="text-sm text-muted-foreground">
            {draftLoadError
              ? "This draft has expired (drafts live for 1 hour). "
              : "Wizard state lost. "}
            <button className="underline" onClick={() => goToStep(1)}>
              Start again
            </button>
            .
          </div>
        )}
        {step === 3 && compiled && step3IR && (
          <Step3Commit
            draftId={compiled.draft_id}
            ir={step3IR}
            irOverrides={irDiff(compiled.ir, step3IR)}
            suggestedThreshold={compiled.suggested_candidate_threshold}
            onSaved={(id) => {
              // Audit F1: success path cleans up the sessionStorage stash;
              // we don't need it once the monitor is persisted.
              clearStashedIR(compiled.draft_id);
              navigate(`/monitors/${id}`);
            }}
          />
        )}
        {step === 3 && (!compiled || !step3IR) && draftIdParam && !draftLoadError && (
          <div className="text-sm text-muted-foreground">Recovering draft…</div>
        )}
        {step === 3 && (!compiled || !step3IR) && (!draftIdParam || draftLoadError) && (
          <div className="text-sm text-muted-foreground">
            {draftLoadError
              ? "This draft has expired (drafts live for 1 hour). "
              : "Wizard state lost. "}
            <button className="underline" onClick={() => goToStep(1)}>Start again</button>.
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }: { step: number }) {
  const labels = ["Write statement", "Backtest preview", "Save"];
  return (
    <div className="flex gap-4">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        return (
          <div key={label} className={active ? "font-semibold" : "text-muted-foreground"}>
            <span
              className={`inline-block w-6 h-6 rounded-full text-center text-xs me-2 ${active ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            >
              {n}
            </span>
            {label}
          </div>
        );
      })}
    </div>
  );
}
